import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import AdminGate from "../components/admin/AdminGate";
import { adminFetch, forceReauth } from "../lib/admin";

/**
 * AdminInboxPage — private correspondence inbox at /admin/inbox.
 * Authentication is provided by the shared AdminGate (same token as the
 * content manager). Contacts only — newsletter subscribers stay API-only.
 */

const ease = [0.16, 1, 0.3, 1];

function formatDate(value) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return (
        d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
        " · " +
        d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    );
}

function Inbox() {
    const [contacts, setContacts] = useState([]);
    const [listError, setListError] = useState(null);
    const [listBusy, setListBusy] = useState(false);
    const [confirmId, setConfirmId] = useState(null);

    const loadContacts = useCallback(async () => {
        setListBusy(true);
        setListError(null);
        try {
            const resp = await adminFetch("/admin/contacts");
            if (resp.status === 401) { forceReauth(); return; }
            if (!resp.ok) { setListError(`Could not load the inbox (HTTP ${resp.status}).`); return; }
            setContacts(await resp.json());
        } catch (_e) {
            setListError("Network error — the inbox could not be reached.");
        } finally {
            setListBusy(false);
        }
    }, []);

    useEffect(() => { loadContacts(); }, [loadContacts]);

    const markRead = async (id, read) => {
        try {
            const resp = await adminFetch(`/admin/contacts/${id}?read=${read}`, { method: "PATCH" });
            if (resp.status === 401) { forceReauth(); return; }
            if (resp.ok) setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, read } : c)));
        } catch (_e) { /* noop */ }
    };

    const remove = async (id) => {
        try {
            const resp = await adminFetch(`/admin/contacts/${id}`, { method: "DELETE" });
            if (resp.status === 401) { forceReauth(); return; }
            if (resp.ok) setContacts((cs) => cs.filter((c) => c.id !== id));
        } catch (_e) { /* noop */ }
        setConfirmId(null);
    };

    const unread = contacts.filter((c) => !c.read).length;

    return (
        <div className="mt-10" data-testid="admin-inbox">
            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[color:var(--line-strong)] pt-6">
                <p
                    data-testid="admin-inbox-count"
                    className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]"
                >
                    {contacts.length} {contacts.length === 1 ? "message" : "messages"}
                    {unread ? ` · ${unread} unread` : ""}
                </p>
                <button
                    type="button"
                    data-testid="admin-refresh-button"
                    onClick={loadContacts}
                    className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                >
                    {listBusy ? "Loading…" : "Refresh"}
                </button>
            </div>

            {listError && (
                <p data-testid="admin-inbox-error" role="alert" className="mt-6 text-[13px] text-[#9A3B26]">
                    {listError}
                </p>
            )}

            {!listError && contacts.length === 0 && !listBusy && (
                <p
                    data-testid="admin-empty-state"
                    className="font-display italic text-[22px] leading-[1.35] text-[color:var(--navy)]/85 py-16 border-b border-[color:var(--line-strong)]"
                >
                    No correspondence yet.
                </p>
            )}

            <div>
                {contacts.map((c) => (
                    <motion.article
                        key={c.id}
                        data-testid="admin-message-item"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.4, ease }}
                        className={`border-b border-[color:var(--line)] py-7 ${c.read ? "opacity-60" : ""}`}
                    >
                        <div className="flex flex-wrap items-baseline justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                {!c.read && (
                                    <span
                                        data-testid="admin-unread-dot"
                                        aria-label="Unread"
                                        className="shrink-0 inline-block w-[7px] h-[7px] rounded-full bg-[color:var(--gold)]"
                                    />
                                )}
                                <span className="font-display text-[20px] leading-[1.2]">{c.name}</span>
                                <a href={`mailto:${c.email}`} className="link-editorial text-[13px] text-[color:var(--navy)]/80 truncate">
                                    {c.email}
                                </a>
                            </div>
                            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--warm-gray-2)]">
                                {formatDate(c.created_at)}
                            </span>
                        </div>
                        <div className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--warm-gray-2)]">
                            {c.organization ? c.organization : "—"}{" · "}{(c.locale || "en").toUpperCase()}
                        </div>
                        <p className="mt-4 text-[15px] leading-[1.7] text-[color:var(--navy)]/90 whitespace-pre-line max-w-[68ch]">
                            {c.message}
                        </p>
                        <div className="mt-5 flex items-center gap-5">
                            <button
                                type="button"
                                data-testid="admin-mark-read-button"
                                onClick={() => markRead(c.id, !c.read)}
                                className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                            >
                                {c.read ? "Mark unread" : "Mark read"}
                            </button>
                            {confirmId === c.id ? (
                                <>
                                    <button
                                        type="button"
                                        data-testid="admin-confirm-delete-button"
                                        onClick={() => remove(c.id)}
                                        className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[#9A3B26]"
                                    >
                                        Confirm delete
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="admin-cancel-delete-button"
                                        onClick={() => setConfirmId(null)}
                                        className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    data-testid="admin-delete-button"
                                    onClick={() => setConfirmId(c.id)}
                                    className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]"
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                    </motion.article>
                ))}
            </div>
        </div>
    );
}

export default function AdminInboxPage() {
    return (
        <AdminGate title="Correspondence inbox.">
            <Inbox />
        </AdminGate>
    );
}
